import theme from '@/style';
import React from 'react';
import {useTranslation} from 'react-i18next';
import DigitBall from '@/common-pages/mix-lottery/component/digit-ball';
import {
  COLORS,
  INDEX_TO_O,
  RESULT_INDEX,
} from '@/common-pages/mix-lottery/constant';
import {
  LotteryOrderTicket,
  checkType,
} from '@/common-pages/mix-lottery/mix-lottery-service';
import {BasicObject, SafeAny} from '@/types';
import Tag from '@/common-pages/mix-lottery/component/tag';
import WonResult from '@/components/business/won/won';
import {toPriceStr} from '@/utils';
const rightIcon = require('@components/assets/icons/me-list-item/right-icon.webp');

const DEFAULT = ['A', 'B', 'C', 'D'];
const SIDE_TYPE = ['Odd', 'Even', 'Big', 'Small'];
const F_P_C_TYPE = ['Fish', 'Prawn', 'Crab'];

const mergeArrayByField = (arr: SafeAny[], field1: string, field2: string) => {
  const merged = {} as BasicObject;
  for (const obj of arr) {
    const key = obj[field1] + '-' + obj[field2]; // 使用字段值组合作为键
    if (!merged[key]) {
      merged[key] = [];
    }
    merged[key].push(obj);
  }
  return merged;
};

const tagInfo = (id: number, betItem: number) => {
  if (id === 1) {
    if (betItem) {
      const TYPE_INDEX = (betItem - 1) % 4;
      const INDEX = Math.trunc((betItem - 1) / 4);
      return {
        type: DEFAULT[INDEX],
        label: SIDE_TYPE[TYPE_INDEX],
      };
    }
  }
  if (id === 9) {
    const TYPE_INDEX = (betItem - 1) % 3;
    return {
      type: 'D',
      label: F_P_C_TYPE[TYPE_INDEX],
    };
  }
  return {};
};

const SattaLottery = ({
  wonCode = [],
  ticketsLists = [],
  awardAmount = 0,
  isQuick = false,
}: {
  wonCode: string[];
  ticketsLists: LotteryOrderTicket[];
  awardAmount: number;
  isQuick?: boolean;
}) => {
  const {t} = useTranslation();

  const targetData = React.useMemo(() => {
    // const res = ticketsLists.filter(item => item.prize === 0);
    return mergeArrayByField(ticketsLists, 'modeName', 'tabName') || {};
  }, [ticketsLists]);

  const getDigitData = (modeID: number, betNo: string) => {
    if (checkType(modeID).isx4) {
      const indexs = RESULT_INDEX.slice(-4);
      return betNo
        .slice(-4)
        .split('')
        .map((item, _i) => {
          return {
            number: item,
            bg: COLORS[indexs[_i]],
          };
        });
    }
    if (checkType(modeID).isx3) {
      const indexs = RESULT_INDEX.slice(-3);
      return betNo
        .slice(-3)
        .split('')
        .map((item, _i) => {
          return {
            number: item,
            bg: COLORS[indexs[_i]],
          };
        });
    }
    if (checkType(modeID).isx2) {
      const indexs = RESULT_INDEX.slice(-2);
      return betNo
        .slice(-2)
        .split('')
        .map((item, _i) => {
          return {
            number: item,
            bg: COLORS[indexs[_i]],
          };
        });
    }
    if (checkType(modeID).isx1) {
      return betNo
        .slice(-1)
        .split('')
        .map((item, _i) => {
          return {
            number: item,
            bg: theme.basicColor.primary,
          };
        });
    }
    return [];
  };

  const renderItem = (lotteryInfo: LotteryOrderTicket, index: number) => {
    if (lotteryInfo.modeID === 9 || lotteryInfo.modeID === 1) {
      const info = tagInfo(lotteryInfo.modeID, lotteryInfo.betItem);
      return (
        <div key={index} className="mt-1 flex gap-1">
          {info.type && (
            <Tag label={info.label} backgroundColor={COLORS[info.type]} />
          )}
        </div>
      );
    }
    const list = getDigitData(lotteryInfo.modeID, lotteryInfo.betNo);
    return (
      <div key={index} className="flex items-center gap-1">
        {list.map((item, _i) => (
          <DigitBall
            isPrimary={checkType(lotteryInfo.modeID).isX}
            key={_i}
            digit={item.number}
            bg={item.bg}
            type={'A'}
          />
        ))}
      </div>
    );
  };

  const status = React.useMemo(() => {
    return !wonCode.every(v => v.indexOf('-') === -1)
      ? 0
      : ticketsLists.every(v => v.ticketStatus === 2) || isQuick
      ? 4
      : 3;
  }, [wonCode, ticketsLists, isQuick]);

  const winList = React.useMemo<BasicObject>(() => {
    const list = ticketsLists.filter(item => item.prize > 0);
    return mergeArrayByField(list, 'modeName', 'tabName') || {};
  }, [ticketsLists]);

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between bg-[#F4F4F4] px-3 py-2">
        <div className="flex flex-1 justify-center">
          <span className="whitespace-pre-line text-sm font-bold">
            {t('bets-detail.label.drawRes').split(' ').join('\n')}：
          </span>
        </div>
        <div className="flex flex-col gap-1">
          {wonCode.map((item, i) => (
            <div className="flex items-center justify-between" key={i}>
              <span className="font-bold">{INDEX_TO_O[i + 1]}-prize：</span>
              <div className="ml-1 flex gap-1">
                {item.split('').map((pie, _i) => (
                  <DigitBall
                    disabled={
                      RESULT_INDEX.slice(-item.split('').length)[_i] ===
                      'UNUSED'
                    }
                    digit={pie}
                    type={RESULT_INDEX.slice(-item.split('').length)[_i]}
                    key={_i}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      {status === 4 && awardAmount > 0 && (
        <WonResult
          awardAmount={awardAmount}
          style={[theme.margin.topxxl, theme.margin.btms]}
        />
      )}
      {status === 4 && awardAmount === 0 && (
        <div className="pb-4 pt-6 text-center text-[0.9375rem] text-primary">
          <span>{t('game-page.label.guessWrong')}</span>
        </div>
      )}
      {status === 3 && (
        <div className="pb-4 pt-6 text-center text-[0.9375rem] text-primary">
          <span>{t('game-page.label.ticketSetting')}</span>
        </div>
      )}
      {Object.keys(winList).length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {Object.keys(winList).map((item, _i) => {
            const data = winList[item] as LotteryOrderTicket[];
            const totalPrize = data.reduce(
              (total, obj) => total + obj.prize,
              0,
            );
            const totalBet = data.reduce(
              (total, obj) => total + (obj.amount + obj.fee),
              0,
            );
            return (
              <div
                key={_i}
                className="flex rounded-lg px-3 py-1"
                style={{backgroundColor: theme.backgroundColor.betsBgLight}}>
                <div className="flex-1">
                  <span className="font-bold">{item}:</span>
                  <div className="flex flex-wrap gap-2">
                    {data.map((pie, _ii) => renderItem(pie, _ii))}
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-bold">
                    {t('game-page.label.bet')} {toPriceStr(totalBet)}
                  </span>
                  <div className="flex items-center">
                    <span className="font-bold text-[#9FA5AC]">
                      {t('bets-detail.label.won')}{' '}
                    </span>
                    <span
                      className="text-base font-bold"
                      style={{color: theme.basicColor.primary}}>
                      {toPriceStr(totalPrize, {fixed: 2})}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-1">
        {Object.keys(targetData)
          .slice(0, 3)
          .map((item, _i) => {
            const valuesData = targetData[item].slice(0, 6);
            return (
              <div
                key={_i}
                className="mt-0.5 rounded-lg bg-[#F6F7FB] px-3 py-1">
                <span className="font-bold">{item}:</span>
                <div className="flex flex-wrap gap-2">
                  {valuesData.map((pie: LotteryOrderTicket, _ii: number) =>
                    renderItem(pie, _ii),
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {(Object.keys(targetData).length > 3 ||
        Object.keys(targetData).find(item => targetData[item].length > 6)) && (
        <div style={stylesWeb.over}>
          <div className="flex items-center justify-center pb-2">
            <span className="font-bold">View Details</span>
            <img src={rightIcon} alt="" className="ml-2 h-4 w-4" />
          </div>
        </div>
      )}
    </div>
  );
};

const stylesWeb: Record<string, React.CSSProperties> = {
  over: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: '5rem',
    background: 'linear-gradient(180deg, #FFFFFF00 0%, #FFFFFF 100%)',
  },
};

export default SattaLottery;
